import React, { useState } from 'react';
import { Package, AlertTriangle, CheckCircle, Clock, Building, ArrowRight, ChevronDown, ChevronRight, ChevronLeft, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import type { DistributionSuggestion } from '@/types/location.types';

interface DistributionSuggestionsProps {
  suggestions: DistributionSuggestion[];
  onCreateOrder: (suggestions: DistributionSuggestion[]) => Promise<any>;
  loading: boolean;
}

export const DistributionSuggestions: React.FC<DistributionSuggestionsProps> = ({
  suggestions,
  onCreateOrder,
  loading
}) => {
  const [selectedSuggestions, setSelectedSuggestions] = useState<string[]>([]);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());
  const [editedQuantities, setEditedQuantities] = useState<Record<string, number>>({});
  const [currentPage, setCurrentPage] = useState<Record<string, number>>({});
  const itemsPerPage = 10;

  const handleSelectSuggestion = (suggestionId: string, checked: boolean) => {
    if (checked) {
      setSelectedSuggestions(prev => [...prev, suggestionId]);
    } else {
      setSelectedSuggestions(prev => prev.filter(id => id !== suggestionId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSuggestions(suggestions.map(s => s.id));
    } else {
      setSelectedSuggestions([]);
    }
  };

  const handleCreateOrder = async () => {
    const selected = suggestions.filter(s => selectedSuggestions.includes(s.id));
    if (selected.length === 0) return;

    setCreatingOrder(true);
    try {
      await onCreateOrder(selected);
      setSelectedSuggestions([]);
    } finally {
      setCreatingOrder(false);
    }
  };


  const toggleLocationExpanded = (locationId: string) => {
    setExpandedLocations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(locationId)) {
        newSet.delete(locationId);
      } else {
        newSet.add(locationId);
      }
      return newSet;
    });
  };

  const isLocationExpanded = (locationId: string) => {
    return expandedLocations.has(locationId);
  };

  const handleQuantityChange = (suggestionId: string, value: string) => {
    const numValue = parseInt(value) || 0;
    setEditedQuantities(prev => ({
      ...prev,
      [suggestionId]: numValue
    }));
  };

  const getEffectiveQuantity = (suggestion: DistributionSuggestion) => {
    return editedQuantities[suggestion.id] ?? suggestion.suggestedQuantity;
  };

  const getCurrentPage = (sourceKey: string) => {
    return currentPage[sourceKey] || 1;
  };

  const setPageForSource = (sourceKey: string, page: number) => {
    setCurrentPage(prev => ({ ...prev, [sourceKey]: page }));
  };

  const getPaginatedSuggestions = (suggestions: DistributionSuggestion[], sourceKey: string) => {
    const page = getCurrentPage(sourceKey);
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return suggestions.slice(startIndex, endIndex);
  };

  const getTotalPages = (totalItems: number) => {
    return Math.ceil(totalItems / itemsPerPage);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  const getPriorityBadge = (priority: DistributionSuggestion['priority']) => {
    const styles = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    };
    
    const labels = {
      critical: 'Crítico',
      high: 'Alto',
      medium: 'Medio',
      low: 'Bajo'
    };

    return (
      <Badge className={styles[priority]}>
        {labels[priority]}
      </Badge>
    );
  };

  const getPriorityIcon = (priority: DistributionSuggestion['priority']) => {
    switch (priority) {
      case 'critical':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'high':
        return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      case 'medium':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'low':
        return <CheckCircle className="w-4 h-4 text-blue-600" />;
    }
  };

  const selectedSuggestionsData = suggestions.filter(s => selectedSuggestions.includes(s.id));

  // Group suggestions by destination location only
  const suggestionsByLocation = suggestions.reduce((acc, suggestion) => {
    const destinationId = suggestion.locationId;
    
    if (!acc[destinationId]) {
      acc[destinationId] = {
        location: { ...suggestion.location, suggestions: [] },
        criticalCount: 0,
        highCount: 0,
        mediumCount: 0,
        totalSuggestions: 0
      };
    }
    
    // Add suggestion to location
    acc[destinationId].location.suggestions.push(suggestion);
    
    // Count priorities
    acc[destinationId].totalSuggestions += 1;
    
    if (suggestion.priority === 'critical') acc[destinationId].criticalCount++;
    else if (suggestion.priority === 'high') acc[destinationId].highCount++;
    else if (suggestion.priority === 'medium') acc[destinationId].mediumCount++;
    
    return acc;
  }, {} as Record<string, {
    location: any;
    criticalCount: number;
    highCount: number;
    mediumCount: number;
    totalSuggestions: number;
  }>);

  const locationGroups = Object.values(suggestionsByLocation).sort((a, b) => {
    // Sort by priority: locations with more critical items first
    if (a.criticalCount !== b.criticalCount) return b.criticalCount - a.criticalCount;
    if (a.highCount !== b.highCount) return b.highCount - a.highCount;
    return b.mediumCount - a.mediumCount;
  });

  // Removed auto-expansion - locations are now collapsed by default

  if (suggestions.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            ¡Excelente! No hay sugerencias pendientes
          </h3>
          <p className="text-gray-500">
            Todas las ubicaciones tienen stock adecuado en este momento.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con acciones */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-macartur-primary" />
                Sugerencias de Distribución
              </CardTitle>
              <CardDescription>
                {suggestions.length} transferencias sugeridas para optimizar inventarios
              </CardDescription>
            </div>
            
            {suggestions.length > 0 && (
              <div className="flex items-center gap-2">
                <div className="text-sm text-gray-600">
                  {selectedSuggestions.length} de {suggestions.length} seleccionadas
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSelectAll(selectedSuggestions.length < suggestions.length)}
                >
                  {selectedSuggestions.length === suggestions.length ? 'Deseleccionar' : 'Seleccionar'} Todo
                </Button>
                <Button
                  onClick={handleCreateOrder}
                  disabled={selectedSuggestions.length === 0 || creatingOrder}
                  className="bg-macartur-primary hover:bg-macartur-primary/90"
                >
                  {creatingOrder ? 'Creando...' : 'Crear Orden'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        {/* Resumen de selección */}
        {selectedSuggestions.length > 0 && (
          <CardContent className="border-t bg-blue-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-600 font-medium">Items seleccionados:</span>
                <div className="font-bold">{selectedSuggestions.length}</div>
              </div>
              <div>
                <span className="text-blue-600 font-medium">Ubicaciones:</span>
                <div className="font-bold">
                  {new Set(selectedSuggestionsData.map(s => s.locationId)).size}
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Lista de sugerencias agrupadas por ubicación */}
      <div className="space-y-6">
        {locationGroups.map((group) => {
          const locationExpanded = isLocationExpanded(group.location.id);
          
          return (
            <Card key={group.location.id} className="border-l-4 border-l-macartur-primary">
              <CardHeader>
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleLocationExpanded(group.location.id)}
                >
                  <div className="flex items-center gap-3">
                    <Building className="w-6 h-6 text-macartur-primary" />
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{group.location.name}</CardTitle>
                        {locationExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                      <CardDescription className="flex items-center gap-1">
                        {group.location.suggestions?.[0]?.centralLocationName && (
                          <>
                            <span className="font-medium">Origen:</span>{' '}
                            {group.location.suggestions[0].centralLocationName}
                            <ArrowRight className="w-3 h-3 mx-1" />
                          </>
                        )}
                        {group.totalSuggestions} transferencias sugeridas
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {group.criticalCount > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {group.criticalCount} crítico{group.criticalCount > 1 ? 's' : ''}
                      </Badge>
                    )}
                    {group.highCount > 0 && (
                      <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-800">
                        {group.highCount} alto{group.highCount > 1 ? 's' : ''}
                      </Badge>
                    )}
                    {group.mediumCount > 0 && (
                      <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800">
                        {group.mediumCount} medio{group.mediumCount > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
            
            {locationExpanded && (
              <CardContent>
                <div className="bg-white rounded-lg border">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left py-3 px-4 font-medium text-gray-600 text-sm w-12">
                            <Checkbox
                              checked={group.location.suggestions?.every(s => selectedSuggestions.includes(s.id)) || false}
                              onCheckedChange={(checked) => {
                                group.location.suggestions?.forEach(s => {
                                  handleSelectSuggestion(s.id, checked as boolean);
                                });
                              }}
                            />
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600 text-sm">Ingrediente</th>
                          <th className="text-center py-3 px-2 font-medium text-gray-600 text-sm w-16">Unidad</th>
                          <th className="text-right py-3 px-4 font-medium text-gray-600 text-sm">Stock actual</th>
                          <th className="text-right py-3 px-4 font-medium text-gray-600 text-sm">Stock mínimo</th>
                          <th className="text-right py-3 px-4 font-medium text-gray-600 text-sm">Consumo hoy</th>
                          <th className="text-right py-3 px-4 font-medium text-gray-600 text-sm">Consumo ayer</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600 text-sm">Cantidad sugerida</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPaginatedSuggestions(group.location.suggestions || [], group.location.id).map((suggestion) => (
                          <tr key={suggestion.id} className="border-b hover:bg-gray-50 transition-colors">
                            <td className="py-3 px-4">
                              <Checkbox
                                checked={selectedSuggestions.includes(suggestion.id)}
                                onCheckedChange={(checked) => 
                                  handleSelectSuggestion(suggestion.id, checked as boolean)
                                }
                              />
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                {getPriorityIcon(suggestion.priority)}
                                <div>
                                  <div className="font-medium text-gray-900 text-sm">
                                    {suggestion.ingredient.name}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-center">
                              <span className="text-xs text-gray-600 font-medium">
                                {suggestion.ingredient.unit}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="font-medium text-red-600 text-sm">
                                {suggestion.currentStock}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="font-medium text-gray-900 text-sm">
                                {suggestion.minStock}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="font-medium text-green-600 text-sm">
                                {suggestion.todayConsumption}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="font-medium text-gray-600 text-sm">
                                {suggestion.yesterdayConsumption}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Input
                                type="number"
                                min="0"
                                max={suggestion.centralStock}
                                value={getEffectiveQuantity(suggestion)}
                                onChange={(e) => handleQuantityChange(suggestion.id, e.target.value)}
                                className="w-16 h-8 text-sm text-center"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination Controls */}
                  {(group.location.suggestions?.length || 0) > itemsPerPage && (
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
                      <div className="text-sm text-gray-700">
                        Mostrando {Math.min((getCurrentPage(group.location.id) - 1) * itemsPerPage + 1, group.location.suggestions?.length || 0)} - {Math.min(getCurrentPage(group.location.id) * itemsPerPage, group.location.suggestions?.length || 0)} de {group.location.suggestions?.length || 0} items
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPageForSource(group.location.id, getCurrentPage(group.location.id) - 1)}
                          disabled={getCurrentPage(group.location.id) <= 1}
                          className="h-8"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        
                        <span className="text-sm font-medium text-gray-700 px-2">
                          Página {getCurrentPage(group.location.id)} de {getTotalPages(group.location.suggestions?.length || 0)}
                        </span>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPageForSource(group.location.id, getCurrentPage(group.location.id) + 1)}
                          disabled={getCurrentPage(group.location.id) >= getTotalPages(group.location.suggestions?.length || 0)}
                          className="h-8"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};